import { create } from 'zustand';
import type { Batch, Bill, Customer, Movement, Offer, Product, StaffMember, Supplier } from '@sense/shared';
import { api, ApiError } from './api';
import { billStockDeltas, db, getMeta, setMeta, type LedgerRow } from './db';
import { useSession } from './session';
import { isLocalOnly } from './local';

interface SyncState {
  online: boolean;
  syncing: boolean;
  pending: number;
  lastSync: number | null;
  error: string | null;
  catalogVersion: number;
  customersVersion: number;
}

export const useSync = create<SyncState>(() => ({
  online: navigator.onLine,
  syncing: false,
  pending: 0,
  lastSync: null,
  error: null,
  catalogVersion: 0,
  customersVersion: 0,
}));

// Bills go up every 2 minutes (or at once when 20 are waiting): few requests per till, so 1,000 stores stay cheap.
export const FLUSH_EVERY_MS = 120_000;
const FLUSH_AT_BILLS = 20;
const STOCK_EVERY_MS = 10 * 60_000;

export async function refreshPending() {
  useSync.setState({ pending: await db.outbox.count() });
}

/** Pull catalog rows changed since our version. 10k products arrive in a few pages on first run. */
export async function pullCatalog(force = false) {
  let since = force ? 0 : await getMeta<number>('catalogVersion', 0);
  for (let guard = 0; guard < 20; guard++) {
    const r = await api<{ products: Product[]; offers: Offer[]; suppliers?: Supplier[]; version: number; more: boolean }>(`/api/catalog?since=${since}`);
    if (r.products.length) await db.products.bulkPut(r.products);
    if (r.offers?.length) await db.offers.bulkPut(r.offers);
    if (r.suppliers?.length) await db.suppliers.bulkPut(r.suppliers);
    since = r.version;
    await setMeta('catalogVersion', since);
    if (!r.more) break;
  }
  useSync.setState({ catalogVersion: since });
}

/** Customers changed since our version (directory for the billing picker, balances, points). */
export async function pullCustomers() {
  let since = await getMeta<number>('customersVersion', 0);
  for (let guard = 0; guard < 20; guard++) {
    const r = await api<{ customers: Customer[]; version: number; more: boolean }>(`/api/customers?since=${since}`);
    if (r.customers.length) await db.customers.bulkPut(r.customers);
    since = r.version;
    await setMeta('customersVersion', since);
    if (!r.more) break;
  }
}

/** The server merged a customer we made offline into one that already had that phone number. */
async function remapCustomers(map: Record<string, string>) {
  for (const [from, to] of Object.entries(map)) {
    await db.customers.delete(from);
    await db.outbox.toCollection().modify((row) => {
      if (row.kind === 'bill' && (row.doc as Bill).customer_id === from) (row.doc as Bill).customer_id = to;
      if (row.kind === 'ledger' && (row.doc as LedgerRow).customer_id === from) (row.doc as LedgerRow).customer_id = to;
    });
    await db.ledger.where('customer_id').equals(from).modify({ customer_id: to });
    await db.bills.toCollection().modify((b) => { if (b.customer_id === from) b.customer_id = to; });
  }
}

/** Staff list with PIN hashes, so the till can unlock offline. */
export async function pullStaff() {
  if (isLocalOnly()) return; // Starter is owner-only on one phone
  const r = await api<{ staff: Array<StaffMember & { active: boolean; canSignIn: boolean }> }>('/api/staff');
  await db.transaction('rw', db.staff, async () => {
    await db.staff.clear();
    await db.staff.bulkPut(r.staff);
  });
  await setMeta('staffAt', Date.now());
}

/** Server stock + our not-yet-uploaded movements = what is on the shelf now. */
export async function pullStock() {
  const r = await api<{ stock: Record<string, number> }>('/api/stock');
  const pending = new Map<number, number>();
  for (const row of await db.outbox.toArray()) {
    if (row.kind !== 'bill' && row.kind !== 'movement') continue;
    const deltas = row.kind === 'bill'
      ? billStockDeltas((row.doc as Bill).items, (row.doc as Bill).kind)
      : (row.doc as Movement).lines.map(([pid, q]) => [pid, q] as [number, number]);
    for (const [pid, q] of deltas) pending.set(pid, (pending.get(pid) ?? 0) + q);
  }
  const ids = new Set([...Object.keys(r.stock).map(Number), ...pending.keys()]);
  await db.transaction('rw', db.stock, async () => {
    await db.stock.clear();
    await db.stock.bulkPut([...ids].map((pid) => ({ pid, qty: (r.stock[pid] ?? 0) + (pending.get(pid) ?? 0) })));
  });
  await setMeta('stockAt', Date.now());
  await pullBatches().catch(() => {});
}

/** Fresh-stock batches expiring in the next two weeks (bakery, dairy, produce). */
export async function pullBatches() {
  if (isLocalOnly()) return; // batches are made on the phone (saveMovement)
  const r = await api<{ batches: Batch[] }>('/api/batches?days=14');
  await db.transaction('rw', db.batches, async () => {
    await db.batches.clear();
    await db.batches.bulkPut(r.batches);
  });
}

let running: Promise<void> | null = null;

/**
 * Phone-only (Starter): nothing goes up and nothing comes down, except one download when a phone
 * signs in with no data yet (part of signing in, like Sense Fresh), so the till isn't empty.
 */
async function seedPhoneOnce() {
  if (await getMeta<boolean>('phoneSeeded', false)) return;
  if (!(await db.products.count())) {
    await pullCatalog(true);
    await pullStock();
    await pullCustomers();
  }
  await setMeta('phoneSeeded', true);
}

export function syncNow(): Promise<void> {
  if (!useSession.getState().token) return Promise.resolve();
  if (isLocalOnly()) {
    running ??= seedPhoneOnce().catch(() => {}).finally(() => { running = null; });
    return running;
  }
  running ??= (async () => {
    useSync.setState({ syncing: true });
    try {
      // 1. Upload in batches until the outbox is empty.
      for (let guard = 0; guard < 50; guard++) {
        const bills = await db.outbox.where('kind').equals('bill').limit(50).toArray();
        const moves = await db.outbox.where('kind').equals('movement').limit(20).toArray();
        const custs = await db.outbox.where('kind').equals('customer').limit(50).toArray();
        const shifts = await db.outbox.where('kind').equals('shift').limit(10).toArray();
        if (!bills.length && !moves.length && !custs.length && !shifts.length) break;
        const r = await api<{ accepted: { bills: number; movements: number }; catalogVersion: number; customersVersion: number; customerIds: Record<string, string>; status?: string }>('/api/sync', {
          method: 'POST',
          json: { bills: bills.map((b) => b.doc), movements: moves.map((m) => m.doc), customers: custs.map((c) => c.doc), shifts: shifts.map((x) => x.doc) },
        });
        if (Object.keys(r.customerIds ?? {}).length) await remapCustomers(r.customerIds);
        // Approved, paused or closed by the platform since we last looked: refresh the profile.
        const me = useSession.getState().me;
        if (r.status && me && r.status !== me.store.status) void useSession.getState().loadMe().catch(() => {});
        useSync.setState({ customersVersion: r.customersVersion });
        // Customers and shifts are upserts, so everything sent is done; bills/movements go by device cursor.
        const done = [...bills, ...moves, ...custs, ...shifts].filter((row) =>
          row.kind === 'customer' || row.kind === 'shift' ? true
            : row.kind === 'bill' ? (row.doc as Bill).dev_no <= r.accepted.bills : (row.doc as Movement).dev_no <= r.accepted.movements);
        await db.transaction('rw', db.outbox, db.bills, async () => {
          await db.outbox.bulkDelete(done.map((d) => d.seq!));
          for (const d of done) if (d.kind === 'bill') await db.bills.update((d.doc as Bill).id, { synced: 1 });
        });
        useSync.setState({ catalogVersion: r.catalogVersion });
        if (done.length === 0) break;
      }
      // 2. Catalog deltas (only when the server says something changed).
      const local = await getMeta<number>('catalogVersion', 0);
      const { catalogVersion } = useSync.getState();
      if (catalogVersion > local || local === 0) await pullCatalog();
      if (useSync.getState().customersVersion > (await getMeta<number>('customersVersion', 0)) || !(await db.customers.count())) await pullCustomers();
      // 3. Stock, at most every few minutes.
      if (Date.now() - (await getMeta<number>('stockAt', 0)) > STOCK_EVERY_MS) await pullStock();
      if (Date.now() - (await getMeta<number>('staffAt', 0)) > STOCK_EVERY_MS) await pullStaff();
      useSync.setState({ lastSync: Date.now(), error: null, online: true });
    } catch (e) {
      const offline = e instanceof ApiError && e.status === 0;
      useSync.setState({ error: offline ? null : (e as Error).message, online: !offline && navigator.onLine });
    } finally {
      await refreshPending();
      useSync.setState({ syncing: false });
      running = null;
    }
  })();
  return running;
}

/** Called after each saved bill: flush early when enough bills are queued. */
export async function afterQueued() {
  await refreshPending();
  if (useSync.getState().pending >= FLUSH_AT_BILLS) void syncNow();
}

let started = false;
export function startSyncLoop() {
  if (started) return;
  started = true;
  void refreshPending();
  void getMeta<number>('catalogVersion', 0).then((v) => useSync.setState({ catalogVersion: v }));
  void syncNow();
  setInterval(() => void syncNow(), FLUSH_EVERY_MS);
  window.addEventListener('online', () => { useSync.setState({ online: true }); void syncNow(); });
  window.addEventListener('offline', () => useSync.setState({ online: false }));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void syncNow(); });
}
